import { Icons, Images } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomChart from "shared/components/common/customChart";
import CustomTable from "shared/components/common/customTable";
import YearPicker from "shared/components/common/yearPicker";
import StatsCard from "shared/components/dashboard/statsCard";
import SkeletonLoader from "shared/loaders/skeleton";
import { routeConstant } from "shared/routes/routeConstant";
import { GetLatestUsers, GetStats } from "shared/services/dashboard";
import { GetAllTransactions } from "shared/services/transactions";
import { convertToMonthGraphData } from "shared/utils/helper";
import styles from "./styles.module.scss";
import { GetPowerSellerRequestService } from "shared/services/powerSellerRequests";

const imagesTableHeads = [
  { title: "#", key: "sr" },
  { title: "Transaction Id", key: "id" },
  { title: "Buyer", key: "from_user" },
  { title: "Total", key: "total" },
];

const DashboardHome = () => {
  const [activeDate, setActiveDate] = useState<{
    name: string | number;
    value: string | number;
  }>({
    name: "",
    value: "",
  });
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(true);
  const [powerSellerLoading, setPowerSellerLoading] = useState<boolean>(true);
  const [openSelection, setOpenSelection] = useState<boolean>(false);
  const [stats, setStats] = useState<any>({
    listingsCount: 0,
    ordersCount: 0,
    usersCount: 0,
  });
  const [users, setUsers] = useState<any[]>([]);
  const [lineChartData, setLineChartData] = useState<any>({});
  const [transactions, setTransactions] = useState<any[]>([]);
  const [powerSellers, setPowerSellers] = useState<any[]>([]);

  const toggleYearPicker = () => setOpenSelection((prev) => !prev);

  const handleGetStats = () => {
    setStatsLoading(true);
    GetStats({
      year: activeDate?.value
        ? Number(activeDate?.value)
        : new Date().getFullYear(),
    })
      .then(({ status, data: { data } }) => {
        if (status) {
          setStats({
            listingsCount: data?.listingsCount,
            ordersCount: data?.ordersCount,
            usersCount: data?.usersCount,
            powerSellerRequestsCount: data?.powerSellerRequestsCount,
            userReportsCount: data?.userReportsCount,
            productReportsCount: data?.productReportsCount,
            featuredListingsCount: data?.featuredListingsCount,
            completedSales: data?.completedSales,
            totalRevenue: data?.totalRevenue,
          });
          setLineChartData(convertToMonthGraphData(data?.usersWithAllMonths));
        }
      })
      .catch((err) => {})
      .finally(() => {
        setStatsLoading(false);
      });
  };

  const handleGetLatestUsers = () => {
    setUsersLoading(true);
    GetLatestUsers()
      .then(({ data: { data }, status }) => {
        if (status) {
          setUsers(data?.users?.length > 0 ? data?.users : []);
        }
      })
      .catch((err) => {})
      .finally(() => {
        setUsersLoading(false);
      });
  };

  const handleGetAllTransactionns = async (page: number) => {
    setTransactionsLoading(true);
    GetAllTransactions({
      page: page,
      limit: 6,
    })
      .then(({ status, data: { data } }) => {
        if (status) {
          setTransactions(data?.data);
        }
      })
      .catch((err) => {})
      .finally(() => {
        setTransactionsLoading(false);
      });
  };

  const handleGetPowerSellers = async () => {
    setPowerSellerLoading(true);
    GetPowerSellerRequestService(1, 4, "accepted")
      .then(({ status, data: { data } }) => {
        if (status) {
          setPowerSellers(data?.data);
        }
      })
      .catch((err) => {})
      .finally(() => {
        setPowerSellerLoading(false);
      });
  };

  useEffect(() => {
    handleGetLatestUsers();
    handleGetAllTransactionns(1);
    handleGetPowerSellers();
  }, []);

  useEffect(() => {
    handleGetStats();
    // eslint-disable-next-line
  }, [activeDate?.value]);

  return (
    <div className={classNames("flex flex-col gap-5 w-full h-full")}>
      {/* Stats */}
      <div
        className={classNames(
          styles.stats_section,
          "grid lg:grid-cols-4 md:grid-cols-3 xs:grid-cols-2 grid-cols-1 gap-5"
        )}
      >
        <StatsCard
          title={"Total Orders"}
          count={stats?.ordersCount}
          icon={<Icons.Order />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Completed Orders"}
          count={stats?.completedSales}
          icon={<Icons.DashboardStats1 />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Total Revenue"}
          count={
            stats?.totalRevenue
              ? "$" + String((stats?.totalRevenue / 100).toFixed(2))
              : "$0"
          }
          icon={<Icons.Money />}
          loading={statsLoading}
        />

        <StatsCard
          title={"Total Users"}
          count={stats?.usersCount}
          icon={<Icons.Users />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Total Listings"}
          count={stats?.listingsCount}
          icon={<Icons.Store />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Power Seller Requests"}
          count={stats?.powerSellerRequestsCount}
          icon={<Icons.Lock />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Users Reported"}
          count={stats?.userReportsCount}
          icon={<Icons.Bill />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Products Reported"}
          count={stats?.productReportsCount}
          icon={<Icons.Bill />}
          loading={statsLoading}
        />
        <StatsCard
          title={"Featured Listings"}
          count={stats?.featuredListingsCount}
          icon={<Icons.Tag />}
          loading={statsLoading}
        />
      </div>

      <div className="grid grid-cols-12 gap-4 mt-4">
        <div
          className={classNames(
            styles.salesChartContainer,
            styles.sectionContainer,
            "sm:col-span-8 col-span-12"
          )}
        >
          <div className={classNames(styles.dateContainer, "relative mb-2")}>
            <button className="flex items-center" onClick={toggleYearPicker}>
              {activeDate.name
                ? `Selected Year: ${activeDate.name}`
                : "Choose a Year"}
              <Icons.ChevDown />
            </button>

            <YearPicker
              activeDate={activeDate}
              openSelection={openSelection}
              setOpenSelection={setOpenSelection}
              setActiveDate={setActiveDate}
            />
          </div>
          <h4 className={classNames(styles.chartTitle)}>Users</h4>
          <CustomChart
            data={lineChartData}
            type="line"
            yAxisLabel="Users"
            seriesName="Count"
            curveStyle="smooth"
          />
        </div>

        <div
          className={classNames(
            styles.listContainer,
            styles.sectionContainer,
            "sm:col-span-4 col-span-12"
          )}
        >
          <h4 className={classNames(styles.chartTitle)}>New Users</h4>
          <p className={classNames(styles.desc)}>
            List of recently registered users
          </p>

          <div className={classNames(styles.customerWrapper)}>
            {/* Users */}

            {usersLoading ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => {
                  return (
                    <div
                      key={index}
                      className={classNames(
                        styles.customerItem,
                        "flex items-center gap-2"
                      )}
                    >
                      <SkeletonLoader
                        className={classNames(styles.imgContainer)}
                      />
                      <div className="flex flex-col gap-1">
                        <SkeletonLoader
                          className={classNames(styles.smallTitleLoader)}
                        />
                        <SkeletonLoader
                          className={classNames(styles.descLoader)}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {users?.map((customer, index) => (
                  <div
                    key={index}
                    className={classNames(
                      styles.customerItem,
                      "flex items-center gap-2"
                    )}
                  >
                    <div className={classNames(styles.imgContainer)}>
                      <img
                        src={
                          customer?.profile_image?.fullpath
                            ? customer?.profile_image?.fullpath
                            : Images.DefaultAvatar
                        }
                        alt="customer-img"
                      />
                    </div>
                    <div className="flex flex-col">
                      <h5 className={classNames(styles.smallTitle)}>
                        {customer?.name}
                      </h5>
                      <p className={classNames(styles.desc)}>
                        {customer?.email}
                      </p>
                    </div>
                  </div>
                ))}
                {users?.length > 0 ? (
                  <Link
                    className={classNames(styles.linkStyle)}
                    to={routeConstant.users.path}
                  >
                    View All Users <Icons.ArrowRight />
                  </Link>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div
          className={classNames(
            styles.listContainer,
            styles.sectionContainer,
            "sm:col-span-8 col-span-12"
          )}
        >
          <h4 className={classNames(styles.chartTitle, "mb-3")}>
            Recent Transactions
          </h4>
          <div className={classNames(styles.tableWrapper, "relative")}>
            <CustomTable
              heads={imagesTableHeads}
              title="Transactions"
              isEmpty={!transactionsLoading && !transactions?.length}
            >
              {transactionsLoading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <TableRowLoader key={index} />
                  ))
                : transactions?.map((data, idx) => (
                    <tr key={data.id}>
                      <td>{idx + 1}</td>
                      <td>{data?.id}</td>
                      <td>{data?.from_user?.name}</td>
                      <td>${(data?.total / 100).toFixed(2)}</td>
                    </tr>
                  ))}
            </CustomTable>
          </div>
          {transactions?.length > 0 ? (
            <Link
              className={classNames(styles.linkStyle)}
              to={routeConstant.paymentTransactions.path}
            >
              View All Transactions <Icons.ArrowRight />
            </Link>
          ) : null}
        </div>
        <div
          className={classNames(
            styles.listContainer,
            styles.sectionContainer,
            "sm:col-span-4 col-span-12"
          )}
        >
          <h4 className={classNames(styles.chartTitle)}>
            Power Seller Approvals
          </h4>
          <p className={classNames(styles.desc)}>
            List of recently approved power sellers
          </p>

          <div className={classNames(styles.customerWrapper)}>
            {/* Users */}

            {powerSellerLoading ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => {
                  return (
                    <div
                      key={index}
                      className={classNames(
                        styles.customerItem,
                        "flex items-center gap-2"
                      )}
                    >
                      <SkeletonLoader
                        className={classNames(styles.imgContainer)}
                      />
                      <div className="flex flex-col gap-1">
                        <SkeletonLoader
                          className={classNames(styles.smallTitleLoader)}
                        />
                        <SkeletonLoader
                          className={classNames(styles.descLoader)}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {powerSellers?.map((customer, index) => (
                  <div
                    key={index}
                    className={classNames(
                      styles.customerItem,
                      "flex items-center gap-2"
                    )}
                  >
                    <div className={classNames(styles.imgContainer)}>
                      <img
                        src={
                          customer?.profile_image?.fullpath
                            ? customer?.profile_image?.fullpath
                            : Images.DefaultAvatar
                        }
                        alt="customer-img"
                      />
                    </div>
                    <div className="flex flex-col">
                      <h5 className={classNames(styles.smallTitle)}>
                        {customer?.name}
                      </h5>
                      <p className={classNames(styles.desc)}>
                        {customer?.email}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 4 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardHome;
